import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';
import supabase from '../supabaseClient';

// Hardcoded admin user ID (same as backend)
const ADMIN_USER_ID = 'b9ba4796-1294-427c-9cad-58d7d6c215d0';

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '2rem',
  },
  // Recipe Search styles
  searchSection: {
    marginTop: '1.5rem',
    padding: '1.5rem',
    background: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
  },
  searchHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  searchTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  searchResultsGrid: {
    display: 'grid',
    gap: '1rem',
    marginTop: '1rem',
  },
  searchResultCard: {
    padding: '1rem',
    background: 'white',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
  },
  searchResultInfo: {
    flex: 1,
    minWidth: 0,
  },
  searchResultTitle: {
    fontWeight: '600',
    color: '#333',
    marginBottom: '0.25rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  searchResultSource: {
    fontSize: '0.8rem',
    color: '#52cd92',
    marginBottom: '0.25rem',
  },
  searchResultSnippet: {
    fontSize: '0.85rem',
    color: '#666',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  searchResultActions: {
    display: 'flex',
    gap: '0.5rem',
    flexShrink: 0,
  },
  previewButton: {
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  importButton: {
    backgroundColor: '#52cd92',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  previewModal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  previewContent: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '2rem',
    maxWidth: '800px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem',
  },
  previewStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  statCard: {
    padding: '1rem',
    background: '#f8f9fa',
    borderRadius: '8px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#52cd92',
  },
  statLabel: {
    fontSize: '0.85rem',
    color: '#666',
  },
  ingredientList: {
    maxHeight: '200px',
    overflowY: 'auto',
    marginBottom: '1rem',
  },
  ingredientItem: {
    padding: '0.5rem',
    borderBottom: '1px solid #eee',
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.9rem',
  },
  closeButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
  },
  checkbox: {
    marginRight: '0.75rem',
    transform: 'scale(1.2)',
  },
  batchActions: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    marginTop: '1rem',
    padding: '1rem',
    background: '#e9ecef',
    borderRadius: '8px',
  },
  title: {
    color: '#333',
    marginBottom: '0.5rem',
    fontSize: '2rem',
  },
  subtitle: {
    color: '#666',
    marginBottom: '2rem',
    fontSize: '1rem',
  },
  section: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    color: '#52cd92',
    marginBottom: '1rem',
    fontSize: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  distributionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '1rem',
  },
  dietCard: {
    padding: '1rem',
    background: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
  },
  dietName: {
    fontWeight: '600',
    color: '#333',
    marginBottom: '0.5rem',
  },
  dietCount: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#52cd92',
  },
  dietPercentage: {
    fontSize: '0.875rem',
    color: '#666',
  },
  queryBuilder: {
    marginBottom: '1.5rem',
  },
  queryRow: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  queryColumn: {
    flex: 1,
    minWidth: '300px',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    color: '#333',
    fontWeight: '500',
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '0.5rem',
    maxHeight: '200px',
    overflowY: 'auto',
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    background: '#fafafa',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  button: {
    backgroundColor: '#52cd92',
    color: 'white',
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  buttonSecondary: {
    backgroundColor: '#6c757d',
    color: 'white',
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
  resultCard: {
    padding: '1.5rem',
    background: '#f0fff5',
    borderRadius: '8px',
    border: '2px solid #52cd92',
    marginTop: '1rem',
  },
  resultNumber: {
    fontSize: '3rem',
    fontWeight: '700',
    color: '#52cd92',
  },
  resultLabel: {
    fontSize: '1rem',
    color: '#666',
  },
  resultPercentage: {
    fontSize: '1.25rem',
    color: '#333',
    marginTop: '0.5rem',
  },
  scenarioCard: {
    padding: '1rem',
    background: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
    marginBottom: '1rem',
  },
  scenarioHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  scenarioName: {
    fontWeight: '600',
    color: '#333',
  },
  scenarioResult: {
    textAlign: 'right',
  },
  scenarioCount: {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#52cd92',
  },
  scenarioDetails: {
    fontSize: '0.85rem',
    color: '#666',
    marginTop: '0.5rem',
  },
  tag: {
    display: 'inline-block',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    marginRight: '0.25rem',
    marginBottom: '0.25rem',
  },
  tagStrict: {
    background: '#fff3cd',
    color: '#856404',
  },
  tagNonStrict: {
    background: '#d4edda',
    color: '#155724',
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '0.9rem',
    marginBottom: '0.5rem',
  },
  errorMessage: {
    color: '#ff4d4d',
    padding: '1rem',
    background: '#fff0f0',
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  loadingOverlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    color: '#666',
  },
  tabs: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
    borderBottom: '2px solid #e9ecef',
    paddingBottom: '0.5rem',
  },
  tab: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    background: 'none',
    fontSize: '1rem',
    cursor: 'pointer',
    color: '#666',
    borderBottom: '2px solid transparent',
    marginBottom: '-0.5rem',
    transition: 'all 0.2s',
  },
  tabActive: {
    color: '#52cd92',
    borderBottomColor: '#52cd92',
  },
  recipeList: {
    maxHeight: '400px',
    overflowY: 'auto',
    marginTop: '1rem',
  },
  recipeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.75rem',
    borderBottom: '1px solid #eee',
  },
  recipeImage: {
    width: '50px',
    height: '50px',
    borderRadius: '4px',
    objectFit: 'cover',
    background: '#eee',
  },
  recipeName: {
    fontWeight: '500',
    color: '#333',
  },
  warningBanner: {
    background: '#fff3cd',
    color: '#856404',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  helpText: {
    fontSize: '0.85rem',
    color: '#666',
    fontStyle: 'italic',
    marginTop: '0.5rem',
  },
};

// Food type diets (OR logic in onboarding step 1)
const FOOD_TYPE_DIETS = ['Poultry', 'Seafood', 'Beef', 'Pork', 'Vegetarian', 'Vegan'];

// Dietary restriction diets (AND logic in onboarding step 2)
const RESTRICTION_DIETS = ['Gluten Free', 'Dairy Free', 'Keto', 'Seed Oil Free'];

function DietCoverageDashboard() {
  const navigate = useNavigate();
  const { pgUser, loading: authLoading } = useAuth();
  
  // State
  const [sessionToken, setSessionToken] = useState('');
  const [diets, setDiets] = useState([]);
  const [distribution, setDistribution] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Query builder state
  const [selectedStrictDiets, setSelectedStrictDiets] = useState([]);
  const [selectedNonStrictDiets, setSelectedNonStrictDiets] = useState([]);
  const [queryResult, setQueryResult] = useState(null);
  const [isQuerying, setIsQuerying] = useState(false);
  const [showRecipes, setShowRecipes] = useState(false);
  
  // Saved scenarios
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [scenarioName, setScenarioName] = useState('');
  const [bulkResults, setBulkResults] = useState(null);
  const [isRunningBulk, setIsRunningBulk] = useState(false);
  
  // Recipe Search state
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedForImport, setSelectedForImport] = useState(new Set());
  const [previewingRecipe, setPreviewingRecipe] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  
  // AI Scout state
  const [isAiScouting, setIsAiScouting] = useState(false);
  const [aiScoutResults, setAiScoutResults] = useState(null);
  
  // Tabs
  const [activeTab, setActiveTab] = useState('distribution');
  
  // Check admin access
  useEffect(() => {
    if (!authLoading && pgUser) {
      if (pgUser.id !== ADMIN_USER_ID) {
        navigate('/home');
      }
    }
  }, [pgUser, authLoading, navigate]);
  
  // Initialize
  useEffect(() => {
    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          setSessionToken(session.access_token);
          await Promise.all([
            fetchDiets(session.access_token),
            fetchDistribution(session.access_token)
          ]);
        }
      } catch (err) {
        console.error('Error initializing:', err);
        setError('Failed to initialize dashboard');
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);
  
  const fetchDiets = async (token) => {
    const response = await fetchWithCSRF('/api/v1/admin/diets', {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setDiets(data);
    }
  };
  
  const fetchDistribution = async (token) => {
    const response = await fetchWithCSRF('/api/v1/admin/diet-distribution', {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setDistribution(data);
    }
  };
  
  const handleStrictDietToggle = (dietId) => {
    setSelectedStrictDiets(prev => 
      prev.includes(dietId) 
        ? prev.filter(id => id !== dietId)
        : [...prev, dietId]
    );
  };
  
  const handleNonStrictDietToggle = (dietId) => {
    setSelectedNonStrictDiets(prev => 
      prev.includes(dietId) 
        ? prev.filter(id => id !== dietId)
        : [...prev, dietId]
    );
  };
  
  const runQuery = async () => {
    if (selectedStrictDiets.length === 0 && selectedNonStrictDiets.length === 0) {
      setError('Please select at least one diet');
      return;
    }
    
    setIsQuerying(true);
    setError('');
    
    try {
      const response = await fetchWithCSRF('/api/v1/admin/diet-coverage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          strictDiets: selectedStrictDiets,
          nonStrictDiets: selectedNonStrictDiets,
          includeRecipes: showRecipes
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setQueryResult(data);
      } else {
        const errData = await response.json();
        setError(errData.error || 'Query failed');
      }
    } catch (err) {
      console.error('Query error:', err);
      setError('Failed to run query');
    } finally {
      setIsQuerying(false);
    }
  };
  
  const saveScenario = () => {
    if (!scenarioName.trim()) {
      setError('Please enter a scenario name');
      return;
    }
    
    const newScenario = {
      id: Date.now(),
      name: scenarioName,
      strictDiets: [...selectedStrictDiets],
      nonStrictDiets: [...selectedNonStrictDiets]
    };
    
    setSavedScenarios(prev => [...prev, newScenario]);
    setScenarioName('');
  };
  
  const removeScenario = (id) => {
    setSavedScenarios(prev => prev.filter(s => s.id !== id));
  };
  
  const runBulkAnalysis = async () => {
    if (savedScenarios.length === 0) {
      setError('Please save at least one scenario first');
      return;
    }
    
    setIsRunningBulk(true);
    setError('');
    
    try {
      const response = await fetchWithCSRF('/api/v1/admin/diet-coverage-bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          scenarios: savedScenarios.map(s => ({
            name: s.name,
            strictDiets: s.strictDiets,
            nonStrictDiets: s.nonStrictDiets
          }))
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setBulkResults(data);
      } else {
        const errData = await response.json();
        setError(errData.error || 'Bulk analysis failed');
      }
    } catch (err) {
      console.error('Bulk analysis error:', err);
      setError('Failed to run bulk analysis');
    } finally {
      setIsRunningBulk(false);
    }
  };
  
  const getDietName = (dietId) => {
    const diet = diets.find(d => d.id === dietId);
    return diet ? diet.name : `Diet ${dietId}`;
  };
  
  // Get diet names from selected IDs
  const getSelectedDietNames = () => {
    const names = [];
    selectedStrictDiets.forEach(id => {
      const name = getDietName(id);
      if (name) names.push(name);
    });
    selectedNonStrictDiets.forEach(id => {
      const name = getDietName(id);
      if (name) names.push(name);
    });
    return names;
  };
  
  // Search for recipes matching current diet selection
  const searchForRecipes = async () => {
    const dietNames = getSelectedDietNames();
    if (dietNames.length === 0) {
      setError('Please select at least one diet first');
      return;
    }
    
    setIsSearching(true);
    setError('');
    setSearchResults([]);
    setSelectedForImport(new Set());
    
    try {
      const response = await fetchWithCSRF('/api/v1/admin/recipe-search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          dietNames,
          maxResults: 20
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.recipes || []);
      } else {
        const errData = await response.json();
        setError(errData.error || 'Search failed');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search for recipes');
    } finally {
      setIsSearching(false);
    }
  };
  
  // Preview a recipe before importing
  const previewRecipe = async (url) => {
    setPreviewingRecipe(url);
    setIsLoadingPreview(true);
    setPreviewData(null);
    
    try {
      const response = await fetchWithCSRF('/api/v1/admin/recipe-search/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          url,
          targetDiets: getSelectedDietNames()
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
      } else {
        const errData = await response.json();
        setError(errData.error || 'Failed to preview recipe');
        setPreviewingRecipe(null);
      }
    } catch (err) {
      console.error('Preview error:', err);
      setError('Failed to preview recipe');
      setPreviewingRecipe(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };
  
  // Close preview modal
  const closePreview = () => {
    setPreviewingRecipe(null);
    setPreviewData(null);
  };
  
  // Toggle recipe selection for batch import
  const toggleRecipeSelection = (url) => {
    setSelectedForImport(prev => {
      const newSet = new Set(prev);
      if (newSet.has(url)) {
        newSet.delete(url);
      } else {
        newSet.add(url);
      }
      return newSet;
    });
  };
  
  // Select/deselect all recipes
  const toggleSelectAll = () => {
    if (selectedForImport.size === searchResults.length) {
      setSelectedForImport(new Set());
    } else {
      setSelectedForImport(new Set(searchResults.map(r => r.url)));
    }
  };
  
  // Import a single recipe (redirects to import page)
  const importSingleRecipe = (url) => {
    // Open ImportRecipeFromUrl with the URL pre-filled
    // For now, we'll open it in a new tab
    window.open(`/admin/import-recipe?url=${encodeURIComponent(url)}`, '_blank');
  };
  
  // Run AI Recipe Scout
  const runAiScout = async () => {
    const dietNames = getSelectedDietNames();
    if (dietNames.length === 0) {
      setError('Please select at least one diet first');
      return;
    }
    
    setIsAiScouting(true);
    setError('');
    setAiScoutResults(null);
    setSearchResults([]); // Clear regular search results
    
    try {
      const response = await fetchWithCSRF('/api/v1/admin/ai-recipe-scout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          dietNames,
          checkDuplicates: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiScoutResults(data);
      } else {
        const errData = await response.json();
        setError(errData.error || 'AI Scout failed');
      }
    } catch (err) {
      console.error('AI Scout error:', err);
      setError('Failed to run AI Scout');
    } finally {
      setIsAiScouting(false);
    }
  };
  
  // Quick import from preview (uses preview data directly)
  const quickImportFromPreview = async () => {
    if (!previewData) return;
    
    setIsImporting(true);
    
    try {
      // Prepare the recipe data
      const recipe = {
        name: previewData.parsed_recipe.name,
        description: previewData.parsed_recipe.description,
        prep_time: previewData.parsed_recipe.prep_time,
        cook_time: previewData.parsed_recipe.cook_time,
        servings: previewData.parsed_recipe.servings,
        image_url: previewData.parsed_recipe.image_url,
        calories: previewData.parsed_recipe.nutrition?.calories,
        protein: previewData.parsed_recipe.nutrition?.protein,
        carbohydrate: previewData.parsed_recipe.nutrition?.carbohydrate,
        fat: previewData.parsed_recipe.nutrition?.fat,
        instructions: previewData.parsed_recipe.instructions,
        is_vegetarian: false,
        is_vegan: false
      };
      
      // Prepare ingredients - only include matched ones
      const ingredients = previewData.ingredients
        .filter(ing => ing.matches && ing.matches.length > 0)
        .map(ing => ({
          ingredient_id: ing.matches[0].id,
          quantity: ing.quantity || 1,
          unit: ing.matches[0].unit_of_measurement || 'units',
          notes: ing.notes
        }));
      
      // Prepare steps
      const steps = previewData.parsed_recipe.instructions.map((instruction, index) => ({
        step_number: index + 1,
        instruction
      }));
      
      const response = await fetchWithCSRF('/api/v1/recipe/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({ recipe, ingredients, steps })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Recipe imported successfully! ID: ${data.recipe_id}`);
        closePreview();
        // Refresh coverage data
        fetchDistribution(sessionToken);
      } else {
        const errData = await response.json();
        setError(errData.message || 'Failed to import recipe');
      }
    } catch (err) {
      console.error('Import error:', err);
      setError('Failed to import recipe');
    } finally {
      setIsImporting(false);
    }
  };
  
  const generateCommonScenarios = () => {
    // Find diet IDs by name
    const getDietId = (name) => {
      const diet = diets.find(d => d.name.toLowerCase() === name.toLowerCase());
      return diet ? diet.id : null;
    };
    
    const scenarios = [];
    
    // Generate all combinations of food types with each restriction
    const foodTypes = FOOD_TYPE_DIETS.map(name => ({ name, id: getDietId(name) })).filter(d => d.id);
    const restrictions = RESTRICTION_DIETS.map(name => ({ name, id: getDietId(name) })).filter(d => d.id);
    
    // Single food type with each restriction
    foodTypes.forEach(foodType => {
      restrictions.forEach(restriction => {
        scenarios.push({
          id: Date.now() + Math.random(),
          name: `${foodType.name} + ${restriction.name}`,
          strictDiets: [restriction.id],
          nonStrictDiets: [foodType.id]
        });
      });
    });
    
    // Multiple food types with restrictions
    const commonFoodCombos = [
      ['Poultry', 'Beef'],
      ['Poultry', 'Seafood'],
      ['Poultry', 'Beef', 'Pork'],
      ['Vegetarian'],
      ['Vegan']
    ];
    
    commonFoodCombos.forEach(combo => {
      const foodIds = combo.map(name => getDietId(name)).filter(id => id);
      if (foodIds.length > 0) {
        restrictions.forEach(restriction => {
          scenarios.push({
            id: Date.now() + Math.random(),
            name: `(${combo.join(' OR ')}) + ${restriction.name}`,
            strictDiets: [restriction.id],
            nonStrictDiets: foodIds
          });
        });
        
        // Double restrictions
        for (let i = 0; i < restrictions.length; i++) {
          for (let j = i + 1; j < restrictions.length; j++) {
            scenarios.push({
              id: Date.now() + Math.random(),
              name: `(${combo.join(' OR ')}) + ${restrictions[i].name} + ${restrictions[j].name}`,
              strictDiets: [restrictions[i].id, restrictions[j].id],
              nonStrictDiets: foodIds
            });
          }
        }
      }
    });
    
    setSavedScenarios(scenarios);
  };
  
  // Loading state
  if (authLoading || isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingOverlay}>Loading...</div>
      </div>
    );
  }
  
  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Diet Coverage Dashboard</h1>
      <p style={styles.subtitle}>
        Analyze recipe coverage for different diet combinations to ensure all user preference scenarios are accounted for.
      </p>
      
      {error && <div style={styles.errorMessage}>{error}</div>}
      
      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'distribution' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('distribution')}
        >
          Diet Distribution
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'query' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('query')}
        >
          Query Builder
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'bulk' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('bulk')}
        >
          Bulk Analysis
        </button>
      </div>
      
      {/* Diet Distribution Tab */}
      {activeTab === 'distribution' && distribution && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            Recipe Distribution by Diet
            <span style={{ fontSize: '0.875rem', fontWeight: 'normal', color: '#666' }}>
              ({distribution.totalRecipes} total system recipes)
            </span>
          </h2>
          <div style={styles.distributionGrid}>
            {distribution.diets.map(diet => (
              <div key={diet.id} style={styles.dietCard}>
                <div style={styles.dietName}>{diet.name}</div>
                <div style={styles.dietCount}>{diet.recipe_count}</div>
                <div style={styles.dietPercentage}>{diet.percentage}% of recipes</div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Query Builder Tab */}
      {activeTab === 'query' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Query Builder</h2>
          
          <div style={styles.warningBanner}>
            <strong>How it works:</strong> Non-strict diets use OR logic (recipe matches ANY). 
            Strict diets use AND logic (recipe must match ALL).
          </div>
          
          <div style={styles.queryBuilder}>
            <div style={styles.queryRow}>
              {/* Non-strict (OR) diets - typically food types */}
              <div style={styles.queryColumn}>
                <label style={styles.label}>
                  Food Type Preferences (OR logic)
                  <span style={styles.helpText}>Recipe must match at least ONE</span>
                </label>
                <div style={styles.checkboxGrid}>
                  {diets.filter(d => FOOD_TYPE_DIETS.includes(d.name)).map(diet => (
                    <label key={diet.id} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={selectedNonStrictDiets.includes(diet.id)}
                        onChange={() => handleNonStrictDietToggle(diet.id)}
                      />
                      {diet.name}
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Strict (AND) diets - typically restrictions */}
              <div style={styles.queryColumn}>
                <label style={styles.label}>
                  Dietary Restrictions (AND logic)
                  <span style={styles.helpText}>Recipe must match ALL</span>
                </label>
                <div style={styles.checkboxGrid}>
                  {diets.filter(d => RESTRICTION_DIETS.includes(d.name)).map(diet => (
                    <label key={diet.id} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={selectedStrictDiets.includes(diet.id)}
                        onChange={() => handleStrictDietToggle(diet.id)}
                      />
                      {diet.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Other diets */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={styles.label}>Other Diets</label>
              <div style={styles.checkboxGrid}>
                {diets.filter(d => !FOOD_TYPE_DIETS.includes(d.name) && !RESTRICTION_DIETS.includes(d.name)).map(diet => (
                  <label key={diet.id} style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedStrictDiets.includes(diet.id) || selectedNonStrictDiets.includes(diet.id)}
                      onChange={() => {
                        // Toggle in strict by default for other diets
                        handleStrictDietToggle(diet.id);
                      }}
                    />
                    {diet.name}
                  </label>
                ))}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={showRecipes}
                  onChange={(e) => setShowRecipes(e.target.checked)}
                />
                Include recipe list in results
              </label>
              
              <button
                onClick={runQuery}
                disabled={isQuerying}
                style={{
                  ...styles.button,
                  ...(isQuerying ? styles.buttonDisabled : {})
                }}
              >
                {isQuerying ? 'Running...' : 'Run Query'}
              </button>
              
              <button
                onClick={() => {
                  setSelectedStrictDiets([]);
                  setSelectedNonStrictDiets([]);
                  setQueryResult(null);
                }}
                style={styles.buttonSecondary}
              >
                Clear
              </button>
            </div>
          </div>
          
          {/* Current Query Display */}
          {(selectedStrictDiets.length > 0 || selectedNonStrictDiets.length > 0) && (
            <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <strong>Current Query:</strong>
              {selectedNonStrictDiets.length > 0 && (
                <span> ({selectedNonStrictDiets.map(id => getDietName(id)).join(' OR ')})</span>
              )}
              {selectedStrictDiets.length > 0 && selectedNonStrictDiets.length > 0 && <span> AND </span>}
              {selectedStrictDiets.length > 0 && (
                <span>({selectedStrictDiets.map(id => getDietName(id)).join(' AND ')})</span>
              )}
            </div>
          )}
          
          {/* Query Result */}
          {queryResult && (
            <div style={styles.resultCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={styles.resultNumber}>{queryResult.matchingRecipes}</div>
                  <div style={styles.resultLabel}>matching recipes</div>
                  <div style={styles.resultPercentage}>
                    {queryResult.percentage}% of {queryResult.totalRecipes} total recipes
                  </div>
                </div>
                
                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button 
                    onClick={runAiScout}
                    disabled={isAiScouting}
                    style={{
                      ...styles.button,
                      backgroundColor: '#9c27b0',
                      ...(isAiScouting ? styles.buttonDisabled : {})
                    }}
                  >
                    {isAiScouting ? '🤖 Scouting...' : '🤖 AI Scout'}
                  </button>
                  <button 
                    onClick={searchForRecipes}
                    disabled={isSearching}
                    style={{
                      ...styles.button,
                      backgroundColor: '#2196F3',
                      ...(isSearching ? styles.buttonDisabled : {})
                    }}
                  >
                    {isSearching ? 'Searching...' : '🔍 Basic Search'}
                  </button>
                  <input
                    type="text"
                    value={scenarioName}
                    onChange={(e) => setScenarioName(e.target.value)}
                    placeholder="Scenario name..."
                    style={{ ...styles.input, width: '150px', marginBottom: 0 }}
                  />
                  <button onClick={saveScenario} style={styles.buttonSecondary}>
                    Save Scenario
                  </button>
                </div>
              </div>
              
              {/* Recipe List */}
              {queryResult.recipes && queryResult.recipes.length > 0 && (
                <div style={styles.recipeList}>
                  <strong>Matching Recipes:</strong>
                  {queryResult.recipes.map(recipe => (
                    <div key={recipe.id} style={styles.recipeItem}>
                      {recipe.image_url ? (
                        <img src={recipe.image_url} alt={recipe.name} style={styles.recipeImage} />
                      ) : (
                        <div style={{ ...styles.recipeImage, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                          No img
                        </div>
                      )}
                      <div style={styles.recipeName}>{recipe.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* AI Scout Loading */}
          {isAiScouting && (
            <div style={{
              ...styles.searchSection,
              textAlign: 'center',
              padding: '3rem'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🤖</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                AI Scout is hunting for recipes...
              </div>
              <div style={{ color: '#666' }}>
                Generating smart queries → Searching the web → AI curation → Duplicate check
              </div>
              <div style={{ color: '#999', marginTop: '1rem', fontSize: '0.9rem' }}>
                This may take 15-30 seconds
              </div>
            </div>
          )}
          
          {/* AI Scout Results */}
          {aiScoutResults && (
            <div style={styles.searchSection}>
              <div style={styles.searchHeader}>
                <div style={styles.searchTitle}>
                  🤖 AI Scout Results
                  <span style={{ fontWeight: 'normal', fontSize: '0.9rem', color: '#666', marginLeft: '0.5rem' }}>
                    ({aiScoutResults.elapsed})
                  </span>
                </div>
                <button
                  onClick={() => setAiScoutResults(null)}
                  style={styles.buttonSecondary}
                >
                  Clear
                </button>
              </div>
              
              {/* Stats */}
              <div style={{ 
                display: 'flex', 
                gap: '1.5rem', 
                marginBottom: '1rem', 
                padding: '1rem', 
                background: '#f0f0f0', 
                borderRadius: '8px',
                flexWrap: 'wrap'
              }}>
                <div>
                  <strong>{aiScoutResults.searchResultsCount}</strong>
                  <span style={{ color: '#666', marginLeft: '0.25rem' }}>found</span>
                </div>
                <div>
                  <strong style={{ color: '#52cd92' }}>{aiScoutResults.recipes?.length || 0}</strong>
                  <span style={{ color: '#666', marginLeft: '0.25rem' }}>curated</span>
                </div>
                <div>
                  <strong style={{ color: '#dc3545' }}>{aiScoutResults.rejectedCount}</strong>
                  <span style={{ color: '#666', marginLeft: '0.25rem' }}>rejected</span>
                </div>
                <div>
                  <strong>{aiScoutResults.existingRecipeCount}</strong>
                  <span style={{ color: '#666', marginLeft: '0.25rem' }}>in database</span>
                </div>
              </div>
              
              {/* Rejection reasons */}
              {aiScoutResults.rejectionReasons?.length > 0 && (
                <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#fff3cd', borderRadius: '4px', fontSize: '0.9rem' }}>
                  <strong>Rejection reasons:</strong> {aiScoutResults.rejectionReasons.join(', ')}
                </div>
              )}
              
              {/* Curated Recipes */}
              {aiScoutResults.recipes?.length > 0 ? (
                <div style={styles.searchResultsGrid}>
                  {aiScoutResults.recipes.map((recipe, index) => (
                    <div key={index} style={{
                      ...styles.searchResultCard,
                      borderLeft: '4px solid #9c27b0'
                    }}>
                      <div style={styles.searchResultInfo}>
                        <div style={styles.searchResultTitle} title={recipe.title}>
                          {recipe.title}
                        </div>
                        <div style={{ 
                          fontSize: '0.85rem', 
                          color: '#9c27b0', 
                          marginBottom: '0.5rem',
                          fontStyle: 'italic'
                        }}>
                          {recipe.reason}
                        </div>
                        {recipe.duplicateCheck && recipe.duplicateCheck.confidence > 0 && (
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>
                            Similarity check: {Math.round((1 - recipe.duplicateCheck.confidence) * 100)}% unique
                          </div>
                        )}
                      </div>
                      <div style={styles.searchResultActions}>
                        <button
                          onClick={() => previewRecipe(recipe.url)}
                          style={styles.previewButton}
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => importSingleRecipe(recipe.url)}
                          style={styles.importButton}
                        >
                          Import
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  No recipes passed the AI curation. Try different diet criteria or run again for different results.
                </div>
              )}
              
              {/* Search queries used (collapsible) */}
              <details style={{ marginTop: '1rem' }}>
                <summary style={{ cursor: 'pointer', color: '#666', fontSize: '0.9rem' }}>
                  View search queries used ({aiScoutResults.queries?.length || 0})
                </summary>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem', fontSize: '0.85rem', color: '#666' }}>
                  {aiScoutResults.queries?.map((q, i) => (
                    <li key={i}>{q}</li>
                  ))}
                </ul>
              </details>
            </div>
          )}
          
          {/* Recipe Search Results */}
          {searchResults.length > 0 && (
            <div style={styles.searchSection}>
              <div style={styles.searchHeader}>
                <div style={styles.searchTitle}>
                  🔍 Found {searchResults.length} recipes for: {getSelectedDietNames().join(' + ')}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <label style={{ fontSize: '0.9rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedForImport.size === searchResults.length && searchResults.length > 0}
                      onChange={toggleSelectAll}
                      style={styles.checkbox}
                    />
                    Select All
                  </label>
                  <button
                    onClick={() => setSearchResults([])}
                    style={styles.buttonSecondary}
                  >
                    Clear Results
                  </button>
                </div>
              </div>
              
              <div style={styles.searchResultsGrid}>
                {searchResults.map((result, index) => (
                  <div key={index} style={styles.searchResultCard}>
                    <input
                      type="checkbox"
                      checked={selectedForImport.has(result.url)}
                      onChange={() => toggleRecipeSelection(result.url)}
                      style={styles.checkbox}
                    />
                    <div style={styles.searchResultInfo}>
                      <div style={styles.searchResultTitle} title={result.title}>
                        {result.title}
                      </div>
                      <div style={styles.searchResultSource}>{result.source}</div>
                      <div style={styles.searchResultSnippet}>{result.snippet}</div>
                    </div>
                    <div style={styles.searchResultActions}>
                      <button
                        onClick={() => previewRecipe(result.url)}
                        style={styles.previewButton}
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => importSingleRecipe(result.url)}
                        style={styles.importButton}
                      >
                        Import
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Batch Import Actions */}
              {selectedForImport.size > 0 && (
                <div style={styles.batchActions}>
                  <span style={{ fontWeight: '600' }}>
                    {selectedForImport.size} recipe{selectedForImport.size > 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={() => {
                      // Open each selected URL in the import page
                      selectedForImport.forEach(url => {
                        window.open(`/admin/import-recipe?url=${encodeURIComponent(url)}`, '_blank');
                      });
                    }}
                    style={styles.importButton}
                  >
                    Open in Import Tool ({selectedForImport.size})
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Preview Modal */}
          {previewingRecipe && (
            <div style={styles.previewModal} onClick={closePreview}>
              <div style={styles.previewContent} onClick={(e) => e.stopPropagation()}>
                {isLoadingPreview ? (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <div style={{ fontSize: '1.25rem', color: '#666' }}>Parsing recipe...</div>
                  </div>
                ) : previewData ? (
                  <>
                    <div style={styles.previewHeader}>
                      <div>
                        <h2 style={{ marginBottom: '0.5rem' }}>{previewData.parsed_recipe.name}</h2>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>{previewingRecipe}</div>
                      </div>
                      <button onClick={closePreview} style={styles.closeButton}>✕</button>
                    </div>
                    
                    {previewData.parsed_recipe.image_url && (
                      <img 
                        src={previewData.parsed_recipe.image_url} 
                        alt={previewData.parsed_recipe.name}
                        style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', marginBottom: '1rem', objectFit: 'cover' }}
                      />
                    )}
                    
                    <div style={styles.previewStats}>
                      <div style={styles.statCard}>
                        <div style={styles.statValue}>{previewData.stats.ingredientMatchRate}%</div>
                        <div style={styles.statLabel}>Ingredient Match</div>
                      </div>
                      <div style={styles.statCard}>
                        <div style={styles.statValue}>{previewData.stats.totalIngredients}</div>
                        <div style={styles.statLabel}>Total Ingredients</div>
                      </div>
                      <div style={styles.statCard}>
                        <div style={styles.statValue}>{previewData.stats.matchedIngredients}</div>
                        <div style={styles.statLabel}>Matched</div>
                      </div>
                      {previewData.stats.dietScore && (
                        <div style={{
                          ...styles.statCard,
                          background: previewData.stats.dietScore.score >= 0.7 ? '#d4edda' : 
                                      previewData.stats.dietScore.score >= 0.5 ? '#fff3cd' : '#f8d7da'
                        }}>
                          <div style={{
                            ...styles.statValue,
                            color: previewData.stats.dietScore.score >= 0.7 ? '#155724' : 
                                   previewData.stats.dietScore.score >= 0.5 ? '#856404' : '#721c24'
                          }}>
                            {Math.round(previewData.stats.dietScore.score * 100)}%
                          </div>
                          <div style={styles.statLabel}>Diet Score</div>
                        </div>
                      )}
                    </div>
                    
                    {previewData.stats.dietScore?.flags?.length > 0 && (
                      <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '4px', fontSize: '0.9rem' }}>
                        {previewData.stats.dietScore.flags.map((flag, i) => (
                          <div key={i}>{flag}</div>
                        ))}
                      </div>
                    )}
                    
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Ingredients ({previewData.ingredients.length}):</strong>
                      <div style={styles.ingredientList}>
                        {previewData.ingredients.map((ing, i) => (
                          <div key={i} style={styles.ingredientItem}>
                            <span>{ing.original}</span>
                            {ing.matches && ing.matches.length > 0 ? (
                              <span style={{ color: '#52cd92' }}>
                                → {ing.matches[0].name} ({Math.round(ing.matches[0].confidence * 100)}%)
                              </span>
                            ) : (
                              <span style={{ color: '#dc3545' }}>No match</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                      <button onClick={closePreview} style={styles.buttonSecondary}>
                        Cancel
                      </button>
                      <button
                        onClick={() => importSingleRecipe(previewingRecipe)}
                        style={{ ...styles.importButton, padding: '0.75rem 1.5rem' }}
                      >
                        Open in Import Tool
                      </button>
                      <button
                        onClick={quickImportFromPreview}
                        disabled={isImporting || previewData.stats.ingredientMatchRate < 50}
                        style={{
                          ...styles.button,
                          ...(isImporting || previewData.stats.ingredientMatchRate < 50 ? styles.buttonDisabled : {})
                        }}
                        title={previewData.stats.ingredientMatchRate < 50 ? 'Match rate too low for quick import' : ''}
                      >
                        {isImporting ? 'Importing...' : 'Quick Import'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#dc3545' }}>
                    Failed to load preview
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Bulk Analysis Tab */}
      {activeTab === 'bulk' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Bulk Scenario Analysis</h2>
          
          <div style={{ marginBottom: '1rem' }}>
            <button
              onClick={generateCommonScenarios}
              style={styles.button}
            >
              Generate Common Scenarios
            </button>
            <span style={{ marginLeft: '1rem', color: '#666', fontSize: '0.875rem' }}>
              Auto-generate all common food type + restriction combinations
            </span>
          </div>
          
          {/* Saved Scenarios */}
          {savedScenarios.length > 0 && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <strong>{savedScenarios.length} scenarios saved</strong>
                <button
                  onClick={runBulkAnalysis}
                  disabled={isRunningBulk}
                  style={{
                    ...styles.button,
                    marginLeft: '1rem',
                    ...(isRunningBulk ? styles.buttonDisabled : {})
                  }}
                >
                  {isRunningBulk ? 'Analyzing...' : 'Run All Scenarios'}
                </button>
                <button
                  onClick={() => setSavedScenarios([])}
                  style={{ ...styles.buttonSecondary, marginLeft: '0.5rem' }}
                >
                  Clear All
                </button>
              </div>
              
              {/* Scenario List */}
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {savedScenarios.map((scenario, index) => {
                  const bulkResult = bulkResults?.scenarios?.find(s => s.name === scenario.name);
                  
                  return (
                    <div key={scenario.id} style={styles.scenarioCard}>
                      <div style={styles.scenarioHeader}>
                        <div style={styles.scenarioName}>{scenario.name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          {bulkResult && (
                            <div style={styles.scenarioResult}>
                              <div style={{
                                ...styles.scenarioCount,
                                color: bulkResult.matchingRecipes < 10 ? '#dc3545' : 
                                       bulkResult.matchingRecipes < 30 ? '#ffc107' : '#52cd92'
                              }}>
                                {bulkResult.matchingRecipes} ({bulkResult.percentage}%)
                              </div>
                            </div>
                          )}
                          <button
                            onClick={() => removeScenario(scenario.id)}
                            style={{ ...styles.buttonSecondary, padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <div style={styles.scenarioDetails}>
                        {scenario.nonStrictDiets.length > 0 && (
                          <>
                            <span style={{ marginRight: '0.5rem' }}>OR:</span>
                            {scenario.nonStrictDiets.map(id => (
                              <span key={id} style={{ ...styles.tag, ...styles.tagNonStrict }}>
                                {getDietName(id)}
                              </span>
                            ))}
                          </>
                        )}
                        {scenario.strictDiets.length > 0 && (
                          <>
                            <span style={{ marginRight: '0.5rem', marginLeft: '0.5rem' }}>AND:</span>
                            {scenario.strictDiets.map(id => (
                              <span key={id} style={{ ...styles.tag, ...styles.tagStrict }}>
                                {getDietName(id)}
                              </span>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          
          {/* Bulk Results Summary */}
          {bulkResults && (
            <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f0fff5', borderRadius: '8px' }}>
              <h3 style={{ marginBottom: '1rem' }}>Coverage Summary</h3>
              <p>Total system recipes: <strong>{bulkResults.totalRecipes}</strong></p>
              
              {/* Highlight low coverage scenarios */}
              {(() => {
                const lowCoverage = bulkResults.scenarios.filter(s => s.matchingRecipes < 10);
                const mediumCoverage = bulkResults.scenarios.filter(s => s.matchingRecipes >= 10 && s.matchingRecipes < 30);
                
                return (
                  <>
                    {lowCoverage.length > 0 && (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8d7da', borderRadius: '4px' }}>
                        <strong style={{ color: '#721c24' }}>Low Coverage ({`<`}10 recipes):</strong>
                        <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                          {lowCoverage.map(s => (
                            <li key={s.name}>{s.name}: <strong>{s.matchingRecipes}</strong> recipes</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {mediumCoverage.length > 0 && (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#fff3cd', borderRadius: '4px' }}>
                        <strong style={{ color: '#856404' }}>Medium Coverage (10-29 recipes):</strong>
                        <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                          {mediumCoverage.map(s => (
                            <li key={s.name}>{s.name}: <strong>{s.matchingRecipes}</strong> recipes</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DietCoverageDashboard;
